import {
  type AvailabilityInfo,
  type AvailabilityInfoDTO,
  type AvailabilityInfoListDTO,
  transformAvailability,
} from "~/domains/product/api/pna/AvailabilityInfo";
import { occUrl } from "~/app/api/occ/occFetch";
import { ApiCallPriority, apiGET, apiPOST } from "~/app/api/apiCallsService";
import { BatchSizeProvider } from "~/app/api/batchSizeProvider";
import { toMapFn } from "~/common/utils/reducers";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";

export interface AvailabilityApiData {
  productCode: string;
  packCode?: string;
}

interface AvailabilityQueueElement {
  resolve: (data: AvailabilityInfo) => void;
  reject: (data: unknown) => void;
  data: AvailabilityApiData;
}

export function generateAvailabilityApiKey(data: AvailabilityApiData) {
  return `${data.productCode}-${data.packCode}`;
}

const A_CACHE: Dict<Promise<AvailabilityInfo>> = {};
const queuedACalls: AvailabilityQueueElement[] = [];
let aGroupingScheduled: boolean = false;
let aGroupingTimer: number | null = null;

const ACCOUNT_CONTEXT_REQUEST_ABORTED = "Availability request superseded by account context change";

export function requestAvailabilityDataSingle(data: AvailabilityApiData) {
  const cacheKey = generateAvailabilityApiKey(data);
  let cacheEntry = A_CACHE[cacheKey];

  if (!cacheEntry) {
    const queueElement: Partial<AvailabilityQueueElement> = { data };
    cacheEntry = new Promise((resolve, reject) => {
      queueElement.resolve = resolve;
      queueElement.reject = reject;
    });
    const createdEntry = cacheEntry;
    createdEntry.finally(() => {
      if (A_CACHE[cacheKey] === createdEntry) {
        delete A_CACHE[cacheKey];
      }
    });
    const finalizedQueueElement = queueElement as AvailabilityQueueElement;
    queuedACalls.push(finalizedQueueElement);
    A_CACHE[cacheKey] = cacheEntry;

    if (!aGroupingScheduled) {
      aGroupingScheduled = true;
      aGroupingTimer = window.setTimeout(dispatchApiCall, getRuntimeConfig().batching.interval);
    }
  }

  return cacheEntry;
}

// BatchSizeProvider is stateful (tracks the escalating batch index); construct it lazily on first
// use so it observes the bootstrap-populated batching config rather than module-load defaults.
let pnaBatchSizeProvider: BatchSizeProvider | null = null;
const getPnaBatchSizeProvider = () => {
  if (!pnaBatchSizeProvider) {
    pnaBatchSizeProvider = new BatchSizeProvider(getRuntimeConfig().batching.pna.batches);
  }
  return pnaBatchSizeProvider;
};

function dispatchApiCall() {
  aGroupingTimer = null;
  const itemsToCall = queuedACalls.slice(0, getPnaBatchSizeProvider().getBatchSize());
  queuedACalls.splice(0, itemsToCall.length);

  aGroupingScheduled = false;

  if (itemsToCall.length === 0) {
    return;
  }

  if (!getRuntimeConfig().batching.pna.availabilityEnabled) {
    for (const item of itemsToCall) {
      const promise = apiFetchAvailabilitySingle(item.data.productCode, item.data.packCode);
      promise.then(item.resolve, item.reject);
    }
  } else {
    const promise = apiFetchAvailabilityMultiple(itemsToCall.map((item) => item.data));
    promise.then(
      (data) =>
        itemsToCall.forEach((item) => {
          const fetchedData = data[item.data.productCode];
          item.resolve(fetchedData);
        }),
      (error) => itemsToCall.forEach((item) => item.reject(error)),
    );
  }

  if (queuedACalls.length > 0) {
    aGroupingScheduled = true;
    aGroupingTimer = window.setTimeout(dispatchApiCall, getRuntimeConfig().batching.interval);
  }
}

/** Clears queued/in-flight legacy Availability promises at an account-context boundary. */
export function invalidateAvailabilityRequestCache(): void {
  if (aGroupingTimer !== null) {
    window.clearTimeout(aGroupingTimer);
    aGroupingTimer = null;
  }
  aGroupingScheduled = false;

  const queued = queuedACalls.splice(0, queuedACalls.length);
  queued.forEach((item) => item.reject(new Error(ACCOUNT_CONTEXT_REQUEST_ABORTED)));
  Object.keys(A_CACHE).forEach((cacheKey) => delete A_CACHE[cacheKey]);
}

async function apiFetchAvailabilitySingle(productCode: string, packCode?: string): Promise<AvailabilityInfo> {
  const singleApiUrl = occUrl(`/pna/getAvailability/${productCode}` + (packCode ? `?packCode=${packCode}` : ""));

  const response = await apiGET(
    singleApiUrl,
    {
      cache: "no-cache",
    },
    ApiCallPriority.LOW,
  );

  if (!response.ok) {
    throw new Error("Error fetching data");
  }

  const dto: AvailabilityInfoDTO = await response.json();
  return transformAvailability(dto);
}

const multiApiUrl = occUrl("/pna/getAvailabilityList");

export async function apiFetchAvailabilityMultiple(
  data: AvailabilityApiData[],
): Promise<Record<string, AvailabilityInfo>> {
  const body = data.map((item) => ({
    code: item.productCode,
    packCode: item.packCode,
    quantity: 1,
  }));

  const response = await apiPOST(
    multiApiUrl,
    {
      body: JSON.stringify({ products: body }),
      headers: {
        "content-type": "application/json",
      },
    },
    ApiCallPriority.LOW,
  );

  if (!response.ok) {
    throw new Error("Error fetching data");
  }
  const dto: AvailabilityInfoListDTO = await response.json();
  const transformedData = (dto.items ?? []).map(transformAvailability);

  // noinspection UnnecessaryLocalVariableJS
  const record = toMapFn(transformedData, (item) => item.code);
  return record;
}
